// src/components/admin/AdminLayout.jsx
// Barre du haut + barre latérale de l'espace admin. Les pages s'affichent dans <Outlet />.

import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Users, Mic2, CalendarDays, PlusCircle, Ticket, Wallet,
    Banknote, Inbox, Ban, Megaphone, LogOut, Menu, X, UserCog, Send,
} from "lucide-react";
import { getStatsAdmin } from "../../services/AdminGestionService";
import { ouvrirEspace } from "../../utils/agirPour";

const MENU = [
    {
        titre: "Général", liens: [
            { to: "/admin", label: "Tableau de bord", icone: LayoutDashboard, fin: true },
        ]
    },
    {
        titre: "Communauté", liens: [
            { to: "/admin/utilisateurs", label: "Utilisateurs", icone: Users },
            { to: "/admin/createurs", label: "Créateurs", icone: Mic2 },
        ]
    },
    {
        titre: "Événements", liens: [
            { to: "/admin/evenements", label: "Événements", icone: CalendarDays, fin: true },
            { to: "/admin/evenements/ajouter", label: "Créer un événement", icone: PlusCircle },
            { to: "/admin/reservations", label: "Réservations", icone: Ticket },
        ]
    },
    {
        titre: "Argent", liens: [
            { to: "/admin/finances", label: "Chiffre d'affaires", icone: Wallet },
            { to: "/admin/versements", label: "Versements Stripe", icone: Banknote },
        ]
    },
    {
        titre: "Suivi", liens: [
            { to: "/admin/demandes", label: "Demandes", icone: Inbox, badge: "demandes" },
            { to: "/admin/annulations", label: "Annulations", icone: Ban },
            { to: "/admin/tickets", label: "Tickets & annonces", icone: Megaphone },
        ]
    },
    {
        titre: "Consultants", liens: [
            { to: "/admin/consultants", label: "Consultants", icone: UserCog },
            { to: "/consultant", label: "Publier pour un créateur", icone: Send, agirPour: true },
        ]
    },
];

const TITRES = {
    "/admin": "Tableau de bord",
    "/admin/utilisateurs": "Utilisateurs",
    "/admin/createurs": "Créateurs",
    "/admin/evenements": "Événements",
    "/admin/evenements/ajouter": "Créer un événement",
    "/admin/reservations": "Réservations",
    "/admin/finances": "Chiffre d'affaires",
    "/admin/versements": "Versements Stripe",
    "/admin/demandes": "Demandes des organisateurs",
    "/admin/annulations": "Annulations",
    "/admin/tickets": "Tickets & annonces",
    "/admin/consultants": "Consultants",
};

const AdminLayout = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [ouvert, setOuvert] = useState(false);          // menu mobile
    const [nbDemandes, setNbDemandes] = useState(0);

    const admin = (() => {
        try { return JSON.parse(localStorage.getItem("user_admin") || sessionStorage.getItem("user_admin") || "null"); }
        catch { return null; }
    })();

    useEffect(() => {
        getStatsAdmin().then((s) => setNbDemandes(s.demandes_en_attente || 0)).catch(() => { });
    }, [pathname]);

    useEffect(() => { setOuvert(false); }, [pathname]);

    const deconnexion = () => {
        ["token_admin", "user_admin"].forEach((k) => { localStorage.removeItem(k); sessionStorage.removeItem(k); });
        navigate("/admin/login");
    };

    // L'espace /consultant (et AddEvent2/UpdateEvent2) lit "token" et "role" :
    // on y recopie la session admin avant d'y aller.
    const ouvrirEspacePublication = (e) => {
        e.preventDefault();
        // Espace consultant en mode ADMIN : utilise token_admin,
        // ne touche PAS au "token" créateur
        ouvrirEspace("admin");
        navigate("/consultant");
    };

    const titre = TITRES[pathname.replace(/\/$/, "")] || "Administration";

    const Menu_ = (
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
            {MENU.map((groupe) => (
                <div key={groupe.titre}>
                    <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-white/40">{groupe.titre}</p>
                    <div className="space-y-0.5">
                        {groupe.liens.map(({ to, label, icone: Icone, fin, badge, agirPour }) => (
                            <NavLink key={to} to={to} end={fin}
                                onClick={agirPour ? ouvrirEspacePublication : undefined}
                                className={({ isActive }) =>
                                    `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition ${isActive
                                        ? "bg-white text-[#481699] font-bold shadow-sm"
                                        : "text-white/75 hover:bg-white/10 hover:text-white"}`}
                            >
                                <Icone size={16} />
                                <span className="flex-1">{label}</span>
                                {badge === "demandes" && nbDemandes > 0 && (
                                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                                        {nbDemandes}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </div>
                </div>
            ))}
        </nav>
    );

    return (
        <div className="min-h-screen flex bg-gray-50 font-Poppins">
            {/* ── Barre latérale (ordinateur) ── */}
            <aside className="hidden lg:flex w-64 shrink-0 flex-col fixed inset-y-0 left-0 z-30" style={{ backgroundColor: "#2A0B5E" }}>
                <div className="h-16 flex items-center gap-2 px-6 border-b border-white/10">
                    <span className="text-white text-lg font-black tracking-tight">THRUNDRZ</span>
                    <span className="text-[10px] font-bold bg-white/15 text-white px-2 py-0.5 rounded-full">ADMIN</span>
                </div>
                {Menu_}
            </aside>

            {/* ── Menu mobile ── */}
            {ouvert && (
                <div className="lg:hidden fixed inset-0 z-40 flex">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setOuvert(false)} />
                    <aside className="relative w-64 flex flex-col" style={{ backgroundColor: "#2A0B5E" }}>
                        <div className="h-16 flex items-center justify-between px-5 border-b border-white/10">
                            <span className="text-white font-black">THRUNDRZ ADMIN</span>
                            <button onClick={() => setOuvert(false)} className="text-white/70"><X size={20} /></button>
                        </div>
                        {Menu_}
                    </aside>
                </div>
            )}

            {/* ── Contenu ── */}
            <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
                {/* Barre du haut */}
                <header className="h-16 sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-gray-100 flex items-center gap-3 px-4 lg:px-8">
                    <button className="lg:hidden text-gray-700" onClick={() => setOuvert(true)}><Menu size={22} /></button>
                    <h2 className="text-base font-bold text-gray-900 flex-1 truncate">{titre}</h2>

                    {nbDemandes > 0 && (
                        <button onClick={() => navigate("/admin/demandes")}
                            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-600 text-xs font-bold">
                            <Inbox size={13} /> {nbDemandes} demande(s)
                        </button>
                    )}

                    <div className="flex items-center gap-2 pl-3 border-l border-gray-100">
                        <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold">
                            {(admin?.nomComplet || "A").charAt(0).toUpperCase()}

                        </div>
                        <span className="hidden md:block text-xs font-semibold text-gray-700 max-w-[140px] truncate">
                            {admin?.nomComplet || admin?.email || "Administrateur"}
                        </span>
                        <button onClick={deconnexion} title="Se déconnecter"
                            className="ml-1 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-red-50 hover:text-red-600">
                            <LogOut size={16} />
                        </button>
                    </div>
                </header>

                <main className="flex-1 p-4 lg:p-8 min-w-0">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;